import { formatarCodigoProduto, formatarPeso } from "@workspace/text";
import { useState, useMemo } from "react";
import { nomeDaMesa, NomeDaMesa } from "@/lib/mesa";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Product, Category, Customer } from "@workspace/api-client-react";
import { customFetch } from "@workspace/api-client-react";
import { formatCurrency, formatWhatsApp, precoPeso, parsePorcoes, pesoLabel, temWhatsApp, unidadeMenor } from "@/lib/utils";
import { titleCasePtBR } from "@/lib/textNormalize";
import { aPartirDeComPromocao, getActivePromotion, getActiveQuantityPromo, gruposComPromocao, quantityPromoLabel, type PromoChannel } from "@/lib/promotion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CurrencyInput } from "@/components/ui/currency-input";
import {
  Plus, Minus, Trash2, X, User, UserPlus,
  Printer, MessageCircle, CheckCircle2,
  Banknote, CreditCard, Wallet, Truck, Store as StoreIcon,
  Sparkles, AlertCircle, Loader2, AlertTriangle, MapPin, Pencil, Handshake,
} from "lucide-react";
import { isAvailabilityConfigActive, getAvailableTimeSlotsForDate, proximasDatasEncomenda, primeiroMomentoEncomenda, formatAvailabilityDateLabel, turnosDoDia, type RegraDaEncomenda } from "@/lib/availability";
import { type CapacidadeDoCarrinho, type ItemDeEncomenda, dataCurta, diaOcupado, limiteDoDia, hojeNaLoja, horariosDoCarrinho, modoDoCarrinho, motivoDoDia, prazoDoCarrinho, turnoIndisponivel, turnosDoCarrinho } from "@/lib/encomendaUnica";
import type { ProductAvailabilityConfig } from "@workspace/api-client-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import type { UseDeliveryFeeReturn, DeliveryAddress } from "@/lib/use-delivery-fee";
import {
  computeOptionsTotal,
  groupMinSelections,
  groupsForValidation,
  isValidGroupSelection as isValidGroupSelectionShared,
  optionGroupKey,
  optionInPackage,
  gruposComConta,
  prepCountDaLista,
  prepCountDoItem,
  prepCountFor,
  prepCountLines,
  prepCountOn,
  prepCountSnapshot,
  formatPreparos,
  groupHasWeights,
  groupWeightedCount,
  optionWeight,
  optionWeightLabel,
  selectionsByGroupIndex,
  type OptionGroup,
  HORARIO_DOS_TURNOS_PADRAO,
  NOME_DO_TURNO,
  encomendaPorExtenso,
  faixaDoTurnoTexto,
  modoDaEncomenda,
  turnosDoProduto,
  type HorarioDosTurnos,
  type Turno,
} from "@workspace/pricing";

// Produto com preço-base 0 tem o preço nas opções: mostra «A partir de» o menor, como o card do cardápio (Pedro,
// 23/10/2026: «Tem que corrigir na tela do PDV também»; antes saía R$ 0,00). Com a promoção em % nas opções, o valor
// com desconto e o original riscado. null = preço ou promoção, como sempre.
function aPartirDe(product: any, canal: PromoChannel = "pdv") {
  return aPartirDeComPromocao(product, canal);
}

function PrecoAPartirDe({ ap, tamanho }: { ap: { valor: number; original: number | null }; tamanho: "card" | "sugestao" }) {
  const rotulo = tamanho === "card" ? "text-xs" : "";
  return ap.original != null ? (
    <span className="inline-flex items-baseline gap-1.5 flex-wrap">
      <span className={`${rotulo} font-medium text-muted-foreground`}>A partir de</span>
      <span className="font-bold text-primary">{formatCurrency(ap.valor)}</span>
      <span className={`${tamanho === "card" ? "text-xs" : "text-[10px]"} text-muted-foreground line-through`}>{formatCurrency(ap.original)}</span>
    </span>
  ) : (
    <span className="font-bold text-primary"><span className={`${rotulo} font-medium text-muted-foreground`}>A partir de </span>{formatCurrency(ap.valor)}</span>
  );
}
import { ContadorDePreparos } from "@/components/ContadorDePreparos";

export type SelectedOption = { groupName: string; optionName: string; price: number; groupIndex?: number };

export type CartItem = {
  cartItemId: string;
  product: Product;
  quantity: number;
  notes: string;
  selectedOptions: SelectedOption[];
  removedIngredients: string[];
  optionsTotal: number;
  unitPrice: number;
  originalUnitPrice: number;
  scheduledDate?: string;
  scheduledTime?: string;
  // Turno da encomenda ('manha' | 'tarde' | 'noite') — produto que pede turno (12/10/2026).
  scheduledShift?: string;
  // Venda por peso: kg pesados (unitPrice já é o valor calculado peso × R$/kg).
  weightKg?: number;
  // Item carregado de pedido em edição cujo produto sumiu do catálogo —
  // preço/opções preservados do pedido, mas não dá pra reabrir o modal
  // de opções (sem optionGroups). Só qty/remover.
  ghost?: boolean;
};

export type CustomerSelection =
  | { kind: "avulso" }
  | { kind: "existing"; customer: Customer }
  | { kind: "new"; name: string; whatsapp: string; ddi: string };

export type PaymentPart = { method: string; amount: number };

export const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "confirmado", label: "Confirmado" },
  { value: "em_preparo", label: "Em preparo" },
  { value: "em_separacao", label: "Em separação" },
  { value: "pronto", label: "Pronto" },
  { value: "entregue", label: "Entregue" },
];

// kg no formato pt-BR sem zeros à direita ("12,5", "0,545").
function formatKgPt(n: number): string {
  return String(Math.round(n * 1000) / 1000).replace(".", ",");
}

// Distância com uma casa e vírgula ("12,3"), como o link mostra.
function formatKmPt(n: number): string {
  return n.toFixed(1).replace(".", ",");
}

export function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function parseCurrency(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Math.round(value * 100) / 100;
  const s = String(value).trim();
  const hasComma = s.includes(",");
  let n: number;
  if (hasComma) {
    n = parseFloat(s.replace(/[^\d,]/g, "").replace(/\./g, "").replace(",", "."));
  } else {
    n = parseFloat(s.replace(/[^\d.]/g, ""));
  }
  return isNaN(n) ? 0 : Math.round(n * 100) / 100;
}

export function productHasOptions(p: Product) {
  const pp = p as any;
  // Grupos de opções valem pra qualquer produto (mai/2026); removíveis seguem food-only.
  const hasGroups = Array.isArray(pp.optionGroups) && pp.optionGroups.length > 0;
  const hasRemovable = pp.isFood && Array.isArray(pp.removableIngredients) && pp.removableIngredients.length > 0;
  return hasGroups || hasRemovable;
}

export function getPaymentMethodLabel(key: string) {
  const map: Record<string, string> = {
    Dinheiro: "Dinheiro",
    Pix: "PIX",
    PIX: "PIX",
    "Débito": "Débito",
    "Crédito": "Crédito",
    Debito: "Débito",
    Credito: "Crédito",
  };
  return map[key] || key;
}

export function getCardSurcharge(method: string, amount: number, cfg: any): number {
  if (!cfg || !cfg[method]) return 0;
  const c = cfg[method];
  if (!c.enabled) return 0;
  if (c.type === "percent") return Math.round((amount * c.value) / 100 * 100) / 100;
  return Math.round(c.value * 100) / 100;
}

export function buildSimpleCartItem(product: Product, quantity = 1): CartItem {
  const promo = getActivePromotion(product, new Date(), "pdv");
  const unitPrice = promo.active ? promo.finalPrice : promo.originalPrice;
  return {
    cartItemId: genId(),
    product,
    quantity,
    notes: "",
    selectedOptions: [],
    removedIngredients: [],
    optionsTotal: 0,
    unitPrice,
    originalUnitPrice: promo.originalPrice,
  };
}

export function ProductCard({
  product,
  onClick,
  qtyInCart,
  onQuickAdd,
  onQuickRemove,
  onQuickIncrement,
}: {
  product: Product;
  onClick: () => void;
  qtyInCart: number;
  onQuickAdd: () => void;
  onQuickRemove: () => void;
  onQuickIncrement: () => void;
}) {
  const promo = getActivePromotion(product, new Date(), "pdv");
  const aPartir = aPartirDe(product);
  const p = product as any;
  const hasOptions = productHasOptions(product);
  const isOrderBased = p.isOrderBased;
  // Por peso o saldo vive em stockKg (string numeric); esgotado = <= 0 exato.
  const kgStock = p.soldByWeight && p.stockKg != null ? parseFloat(String(p.stockKg)) : null;
  const stockOut = p.soldByWeight
    ? kgStock !== null && kgStock <= 0
    : p.stock !== null && p.stock !== undefined && p.stock <= 0;
  // O + rápido de produto por peso abre o dialog (pesagem), que trava lá.
  const reachedStock = p.soldByWeight
    ? false
    : p.stock !== null && p.stock !== undefined && qtyInCart >= p.stock;

  return (
    <div
      onClick={onClick}
      className="flex gap-3 p-3 border border-border rounded-lg bg-card hover:border-primary/40 cursor-pointer transition-colors"
      data-testid={`pdv-product-${product.id}`}
    >
      {p.imageUrl && (
        <img src={p.imageUrl} alt={p.name} className="w-16 h-16 object-cover rounded flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{p.name}</div>
        {p.barcode && <div className="text-xs font-mono text-muted-foreground">{formatarCodigoProduto(p.barcode)}</div>}
        {p.description && <div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div>}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
          {aPartir !== null ? (
            <PrecoAPartirDe ap={aPartir} tamanho="card" />
          ) : promo.active ? (
            <>
              <span className="font-bold text-primary">{p.soldByWeight ? precoPeso(promo.finalPrice, p.weightUnit).valor : formatCurrency(promo.finalPrice)}{p.soldByWeight ? <span className="text-xs font-semibold text-muted-foreground">{precoPeso(promo.finalPrice, p.weightUnit).sufixo}</span> : null}</span>
              <span className="text-xs text-muted-foreground line-through">{formatCurrency(promo.originalPrice)}</span>
            </>
          ) : (
            <span className="font-bold text-primary">{p.soldByWeight ? precoPeso(Number(p.price), p.weightUnit).valor : formatCurrency(Number(p.price))}{p.soldByWeight ? <span className="text-xs font-semibold text-muted-foreground">{precoPeso(Number(p.price), p.weightUnit).sufixo}</span> : null}</span>
          )}
          {(() => {
            const qtyLabel = quantityPromoLabel(getActiveQuantityPromo(product, new Date(), "pdv"));
            return qtyLabel
              // max-w-full + quebra: com nowrap a pílula larga estourava a
              // coluna min-w-0 e escorregava por BAIXO do botão +.
              ? <span className="text-[10px] font-bold uppercase bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5 rounded max-w-full">{qtyLabel}</span>
              : null;
          })()}
          {p.soldByWeight && <span className="text-[10px] uppercase bg-muted px-1.5 py-0.5 rounded whitespace-nowrap">por peso</span>}
          {kgStock !== null && (
            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded whitespace-nowrap ${kgStock <= 0 ? "bg-destructive/10 text-destructive font-bold" : "bg-muted"}`}>
              {kgStock <= 0 ? "esgotado" : `resta ${formatKgPt(kgStock)} kg`}
            </span>
          )}
          {hasOptions && <span className="text-[10px] uppercase bg-muted px-1.5 py-0.5 rounded whitespace-nowrap">opções</span>}
          {isOrderBased && <span className="text-[10px] uppercase bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded whitespace-nowrap">encomenda</span>}
        </div>
      </div>
      <div className="self-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {qtyInCart > 0 ? (
          <div className="flex items-center bg-primary/10 border border-primary/30 rounded-full">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onQuickRemove(); }}
              className="w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/20 rounded-full transition-colors"
              data-testid={`pdv-product-quick-remove-${product.id}`}
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="w-7 text-center text-sm font-bold text-primary">{qtyInCart}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onQuickIncrement(); }}
              disabled={reachedStock}
              className="w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/20 rounded-full transition-colors disabled:opacity-40"
              data-testid={`pdv-product-quick-increment-${product.id}`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onQuickAdd(); }}
            disabled={stockOut}
            className="w-8 h-8 flex items-center justify-center border border-primary/40 text-primary rounded-full hover:bg-primary/10 transition-colors disabled:opacity-40"
            data-testid={`pdv-product-quick-add-${product.id}`}
            title="Adicionar ao pedido"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export function CartItemRow({ item, onRemove, onQtyChange, onEdit }: { item: CartItem; onRemove: () => void; onQtyChange: (q: number) => void; onEdit: () => void }) {
  const lineTotal = (item.unitPrice + item.optionsTotal) * item.quantity;
  const hasCustomization = (item.selectedOptions?.length ?? 0) > 0 || (item.removedIngredients?.length ?? 0) > 0 || !!item.notes || !!item.scheduledDate || item.weightKg != null;

  return (
    <div className="border border-border rounded-lg p-3 bg-background" data-testid={`pdv-cart-item-${item.cartItemId}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{(item.product as any).name}</div>
          {item.weightKg != null && (
            <div className="text-xs text-muted-foreground mt-0.5">{(item.product as any).weightUnit === "ml" ? formatarPeso(item.weightKg, "ml") : `${String(item.weightKg.toFixed(3)).replace(".", ",")} kg`}</div>
          )}
          {(() => {
            const prep = prepDoItem(item);
            if (!prep) return null;
            // «Cada lista conta separado» (28/10/2026): cada adicional embaixo da sua lista, com a conta dela no título.
            if (prep.listas) {
              return gruposComConta(item.selectedOptions, prep).map((g, i) => (
                <div key={i} className="text-xs mt-0.5">
                  {g.titulo && <div className="font-medium" data-testid={g.conta ? "pdv-cart-prep-line" : undefined}>{g.titulo}{g.conta ? `: ${g.conta}` : ""}</div>}
                  {g.opcoes.length > 0 && <div className="text-muted-foreground">{g.opcoes.map((o) => (o.count > 1 ? `${o.count}x ${o.nome}` : o.nome)).join(", ")}</div>}
                </div>
              ));
            }
            return prepCountLines(prep).map((l, i) => <div key={i} className="text-xs font-medium mt-0.5" data-testid="pdv-cart-prep-line">{l}</div>);
          })()}
          {item.selectedOptions.length > 0 && !prepDoItem(item)?.listas && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {item.selectedOptions.map((o) => o.optionName).join(", ")}
            </div>
          )}
          {item.removedIngredients.length > 0 && (
            <div className="text-xs text-orange-600 mt-0.5">Sem: {item.removedIngredients.join(", ")}</div>
          )}
          {item.notes && <div className="text-xs text-muted-foreground italic mt-0.5">"{item.notes}"</div>}
          {item.scheduledDate && (
            <div className="text-xs text-amber-700 mt-0.5">Para {encomendaPorExtenso(item.scheduledDate, item.scheduledTime, item.scheduledShift)}</div>
          )}
        </div>
        <button onClick={onRemove} className="text-muted-foreground hover:text-destructive p-1" data-testid={`pdv-remove-${item.cartItemId}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1 border border-border rounded">
          <button onClick={() => onQtyChange(item.quantity - 1)} className="p-1 hover:bg-muted" data-testid={`pdv-qty-minus-${item.cartItemId}`}>
            <Minus className="w-3 h-3" />
          </button>
          <span className="px-2 text-sm font-semibold min-w-[1.5rem] text-center">{item.quantity}</span>
          <button onClick={() => onQtyChange(item.quantity + 1)} className="p-1 hover:bg-muted" data-testid={`pdv-qty-plus-${item.cartItemId}`}>
            <Plus className="w-3 h-3" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {hasCustomization && (
            <button onClick={onEdit} className="text-xs text-primary hover:underline" data-testid={`pdv-edit-${item.cartItemId}`}>Editar</button>
          )}
          <span className="font-semibold text-sm">{formatCurrency(lineTotal)}</span>
        </div>
      </div>
    </div>
  );
}

// Mesa/comanda: rótulo livre + "pagar ao fechar a conta" (pagamento Pendente,
// definido na edição que fecha a mesa).
export function MesaSection({ tableNumber, onTableNumberChange, payLater, onPayLaterChange }: { tableNumber: string; onTableNumberChange: (v: string) => void; payLater: boolean; onPayLaterChange: (v: boolean) => void }) {
  return (
    <div className="space-y-2 pt-2 border-t border-border">
      <Label className="text-xs">Mesa / comanda (opcional)</Label>
      <Input
        value={tableNumber}
        onChange={(e) => onTableNumberChange(e.target.value.slice(0, 20))}
        placeholder="Ex: 5"
        inputMode="numeric"
        data-testid="pdv-table-number"
      />
      {tableNumber.trim() && (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Label className="text-sm font-medium">Pagar ao fechar a conta</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              O pedido fica com pagamento Pendente; a forma é escolhida quando a conta fechar.
            </p>
          </div>
          <Switch className="mt-0.5 shrink-0" checked={payLater} onCheckedChange={onPayLaterChange} data-testid="pdv-pay-later" />
        </div>
      )}
    </div>
  );
}

export type CustomerSearchHook = (term: string, enabled: boolean) => { customers: Customer[] };

// Loja com pontos: cliente sem WhatsApp não junta pontos (planOrderCredits pula `__avulso__`). Frase do Pedro, 18/10/2026.
export const AVISO_PONTOS_SEM_WHATSAPP = "Se você não informar o WhatsApp do cliente, ele não poderá receber os pontos dele.";

export function CustomerSection({ customer, onChange, customerRequired, useCustomerSearch, avisoSemWhatsApp }: { customer: CustomerSelection; onChange: (c: CustomerSelection) => void; customerRequired: boolean; useCustomerSearch: CustomerSearchHook; avisoSemWhatsApp?: string }) {
  const [searchTerm, setSearchTerm] = useState("");

  const { customers } = useCustomerSearch(searchTerm, searchTerm.length >= 2);

  return (
    <div className="space-y-2 pt-2 border-t border-border">
      <Label className="text-xs flex items-center justify-between">
        <span>Cliente {customerRequired && <span className="text-destructive">*</span>}</span>
      </Label>
      {customer.kind === "existing" ? (
        <div className="flex items-center justify-between bg-primary/10 border border-primary/30 rounded p-2">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            <div>
              <div className="text-sm font-medium">{customer.customer.firstName}</div>
              {customer.customer.whatsappNumber && <div className="text-xs text-muted-foreground">{temWhatsApp(customer.customer.whatsappNumber) ? formatWhatsApp(customer.customer.whatsappNumber) : "Sem WhatsApp"}</div>}
            </div>
          </div>
          <button onClick={() => onChange({ kind: "avulso" })} className="text-muted-foreground hover:text-destructive" data-testid="pdv-customer-remove">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : customer.kind === "new" ? (
        <div className="bg-card border border-border rounded p-2 space-y-2">
          <Input
            value={customer.name}
            onChange={(e) => onChange({ kind: "new", name: e.target.value, whatsapp: customer.whatsapp, ddi: customer.ddi })}
            onBlur={(e) => onChange({ kind: "new", name: titleCasePtBR(e.target.value), whatsapp: customer.whatsapp, ddi: customer.ddi })}
            autoCapitalize="words"
            placeholder="Nome"
            data-testid="pdv-customer-new-name"
          />
          <WhatsAppInput
            value={customer.whatsapp}
            onChange={(v) => onChange({ kind: "new", name: customer.name, whatsapp: v, ddi: customer.ddi })}
            ddi={customer.ddi}
            onDdiChange={(d) => onChange({ kind: "new", name: customer.name, whatsapp: customer.whatsapp, ddi: d })}
            placeholder="WhatsApp (opcional)"
          />
          {avisoSemWhatsApp && !customer.whatsapp.replace(/\D/g, "") && (
            <p className="text-xs text-amber-900 dark:text-amber-200" data-testid="pdv-customer-aviso-pontos">{avisoSemWhatsApp}</p>
          )}
          <button onClick={() => { onChange({ kind: "avulso" }); }} className="text-xs text-muted-foreground hover:text-destructive">Cancelar</button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="flex-1 min-w-0 space-y-1">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou WhatsApp"
                className="w-full"
                data-testid="pdv-customer-search-input"
              />
              {searchTerm.length >= 2 && customers.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-border rounded bg-background">
                  {customers.slice(0, 8).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { onChange({ kind: "existing", customer: c }); setSearchTerm(""); }}
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b border-border last:border-0"
                      data-testid={`pdv-customer-pick-${c.id}`}
                    >
                      <div className="font-medium">{c.firstName}</div>
                      {c.whatsappNumber && <div className="text-xs text-muted-foreground">{temWhatsApp(c.whatsappNumber) ? formatWhatsApp(c.whatsappNumber) : "Sem WhatsApp"}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={() => onChange({ kind: "new", name: "", whatsapp: "", ddi: "55" })} data-testid="pdv-customer-new-toggle">
              <UserPlus className="w-3 h-3 mr-1" />Novo
            </Button>
          </div>
          {avisoSemWhatsApp && (
            <p className="text-xs text-amber-900 dark:text-amber-200" data-testid="pdv-customer-aviso-pontos">{avisoSemWhatsApp}</p>
          )}
        </div>
      )}
    </div>
  );
}

// «combinar»: pedido do link de loja sem entrega e sem retirada (19/10/2026). O PDV não cria; na edição, mantém.
export type ModoDoPedido = "retirada" | "entrega" | "combinar";

interface DeliverySectionProps {
  deliveryMode: ModoDoPedido;
  onDeliveryModeChange: (m: ModoDoPedido) => void;
  // Editando pedido «A combinar»: a opção aparece, marcada, ao lado das de sempre.
  podeCombinar?: boolean;
  address: DeliveryAddress;
  onAddressChange: (a: DeliveryAddress) => void;
  store?: any;
  feeInfo?: UseDeliveryFeeReturn;
  finalDeliveryFee?: number;
  freeDeliveryApplied?: boolean;
  allowsPickup?: boolean;
  allowsDelivery?: boolean;
  feeOverride?: number | null;
  onFeeOverrideChange?: (v: number | null) => void;
}

export function DeliverySection({
  deliveryMode,
  onDeliveryModeChange,
  address,
  onAddressChange,
  store,
  feeInfo,
  finalDeliveryFee,
  freeDeliveryApplied,
  allowsPickup = true,
  allowsDelivery = true,
  feeOverride,
  onFeeOverrideChange,
  podeCombinar = false,
}: DeliverySectionProps) {
  const allowFeeEdit = typeof onFeeOverrideChange === "function";
  const [editingFee, setEditingFee] = useState(false);
  const [feeDraft, setFeeDraft] = useState<string>("");
  const [userManualEntry, setUserManualEntry] = useState(false);
  const feeType = feeInfo?.feeType ?? ((store as any)?.deliveryFeeType ?? "fixed");
  const dynamicFee = feeInfo?.dynamicFee ?? null;
  const feeLoading = feeInfo?.feeLoading ?? false;
  const neighborhoodList = feeInfo?.neighborhoodList ?? [];
  const neighborhoodMeta = feeInfo?.neighborhoodMeta ?? { acceptUnlisted: false, unlistedFee: null };
  const selectedNeighborhood = feeInfo?.selectedNeighborhood ?? "";
  const setSelectedNeighborhood = feeInfo?.setSelectedNeighborhood ?? (() => {});
  const googleMapsKey = feeInfo?.googleMapsKey ?? "";
  const richMode = !!feeInfo;

  const useAutocomplete = richMode && !userManualEntry && !!googleMapsKey && (feeType === "distance" || feeType === "fixed" || feeType === "on_request" || (feeType === "neighborhood" && neighborhoodList.length === 0));
  const useNeighborhoodSelector = richMode && feeType === "neighborhood" && neighborhoodList.length > 0 && !userManualEntry;

  return (
    <div className="space-y-2 pt-2 border-t border-border">
      <Label className="text-xs">Modalidade</Label>
      <div className="flex gap-2">
        {allowsPickup && (
          <button
            onClick={() => onDeliveryModeChange("retirada")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded border text-sm ${deliveryMode === "retirada" ? "bg-primary/10 border-primary text-primary" : "border-border bg-card"}`}
            data-testid="pdv-delivery-retirada"
          >
            <StoreIcon className="w-4 h-4" />Retirada
          </button>
        )}
        {allowsDelivery && (
          <button
            onClick={() => onDeliveryModeChange("entrega")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded border text-sm ${deliveryMode === "entrega" ? "bg-primary/10 border-primary text-primary" : "border-border bg-card"}`}
            data-testid="pdv-delivery-entrega"
          >
            <Truck className="w-4 h-4" />Entrega
          </button>
        )}
        {podeCombinar && (
          <button
            onClick={() => onDeliveryModeChange("combinar")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded border text-sm ${deliveryMode === "combinar" ? "bg-primary/10 border-primary text-primary" : "border-border bg-card"}`}
            data-testid="pdv-delivery-combinar"
          >
            <Handshake className="w-4 h-4" />A combinar
          </button>
        )}
      </div>

      {deliveryMode === "entrega" && (
        <div className="space-y-2 pt-2">
          {useNeighborhoodSelector ? (
            <div className="space-y-2">
              <select
                value={selectedNeighborhood}
                onChange={(e) => {
                  setSelectedNeighborhood(e.target.value);
                  onAddressChange({ ...address, neighborhood: e.target.value === "__other__" ? "" : e.target.value });
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="pdv-neighborhood-select"
              >
                <option value="">Selecione o bairro</option>
                {neighborhoodList.map((n) => (
                  <option key={n.name} value={n.name}>{n.name} — {formatCurrency(n.fee)}</option>
                ))}
                {neighborhoodMeta.acceptUnlisted && <option value="__other__">Outro bairro</option>}
              </select>
              {selectedNeighborhood === "__other__" && (
                <Input
                  placeholder="Digite o bairro"
                  value={address.neighborhood}
                  onChange={(e) => onAddressChange({ ...address, neighborhood: e.target.value })}
                  data-testid="pdv-address-neighborhood-custom"
                />
              )}
              <div className="grid grid-cols-3 gap-2">
                <Input value={address.street} onChange={(e) => onAddressChange({ ...address, street: e.target.value })} placeholder="Rua" className="col-span-2" data-testid="pdv-address-street" />
                <Input value={address.number} onChange={(e) => onAddressChange({ ...address, number: e.target.value })} placeholder="Nº" data-testid="pdv-address-number" />
              </div>
              <Input value={address.complement} onChange={(e) => onAddressChange({ ...address, complement: e.target.value })} placeholder="Complemento (opcional)" data-testid="pdv-address-complement" />
              {neighborhoodMeta.acceptUnlisted && (
                <Input
                  placeholder="CEP"
                  value={address.zipCode.replace(/^(\d{5})(\d{1,3})$/, "$1-$2")}
                  onChange={(e) => { const digits = e.target.value.replace(/\D/g, "").slice(0, 8); onAddressChange({ ...address, zipCode: digits }); }}
                  maxLength={10}
                  data-testid="pdv-address-zip"
                />
              )}
            </div>
          ) : useAutocomplete ? (
            <div className="space-y-2">
              <AddressAutocomplete
                googleMapsApiKey={googleMapsKey}
                value={{
                  street: address.street,
                  number: address.number,
                  complement: address.complement,
                  neighborhood: address.neighborhood,
                  city: address.city,
                  state: address.state || "",
                  zipCode: address.zipCode,
                  lat: address.lat,
                  lng: address.lng,
                  formattedAddress: address.formattedAddress || "",
                }}
                onChange={(addr) => {
                  onAddressChange({
                    ...address,
                    street: addr.street,
                    number: addr.number,
                    complement: addr.complement,
                    neighborhood: addr.neighborhood,
                    city: addr.city,
                    state: addr.state,
                    zipCode: addr.zipCode,
                    lat: addr.lat,
                    lng: addr.lng,
                    formattedAddress: addr.formattedAddress,
                  });
                  if (feeType === "neighborhood" && addr.neighborhood) {
                    setSelectedNeighborhood(addr.neighborhood);
                  }
                }}
                showMap={feeType === "distance"}
                mapHeight={180}
                placeholder="Buscar endereço de entrega..."
                compact={true}
                hideManualToggle={true}
              />
              <button
                type="button"
                onClick={() => setUserManualEntry(true)}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <Pencil className="w-3 h-3" />
                Digitar manualmente
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {richMode && (
                <Input
                  placeholder="CEP"
                  value={address.zipCode.replace(/^(\d{5})(\d{1,3})$/, "$1-$2")}
                  onChange={(e) => { const digits = e.target.value.replace(/\D/g, "").slice(0, 8); onAddressChange({ ...address, zipCode: digits }); }}
                  maxLength={10}
                  data-testid="pdv-address-zip"
                />
              )}
              <div className="grid grid-cols-3 gap-2">
                <Input value={address.street} onChange={(e) => onAddressChange({ ...address, street: e.target.value })} placeholder="Rua" className="col-span-2" data-testid="pdv-address-street" />
                <Input value={address.number} onChange={(e) => onAddressChange({ ...address, number: e.target.value })} placeholder="Nº" data-testid="pdv-address-number" />
              </div>
              <Input value={address.neighborhood} onChange={(e) => onAddressChange({ ...address, neighborhood: e.target.value })} placeholder="Bairro" data-testid="pdv-address-neighborhood" />
              <Input value={address.complement} onChange={(e) => onAddressChange({ ...address, complement: e.target.value })} placeholder="Complemento (opcional)" data-testid="pdv-address-complement" />
              {richMode && address.city && (
                <Input value={address.city} onChange={(e) => onAddressChange({ ...address, city: e.target.value })} placeholder="Cidade" data-testid="pdv-address-city" />
              )}
              {richMode && userManualEntry && googleMapsKey && (
                <button
                  type="button"
                  onClick={() => setUserManualEntry(false)}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <MapPin className="w-3 h-3" />
                  Voltar para busca por endereço
                </button>
              )}
            </div>
          )}

          {richMode && feeLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Calculando taxa de entrega...
            </div>
          )}

          {richMode && feeType === "on_request" && (
            <div className="p-2 rounded border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2">
              <Truck className="w-3 h-3 shrink-0 mt-0.5" />
              <span>Frete será calculado e informado após o pedido.</span>
            </div>
          )}

          {richMode && feeInfo?.feePending && (
            <div className="p-2 rounded border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="pdv-delivery-pending">
              <Truck className="w-3 h-3 shrink-0 mt-0.5" />
              <span>
                Frete a calcular após o pedido
                {dynamicFee?.distanceKm != null
                  ? <> ({formatKmPt(dynamicFee.distanceKm)} km, acima da última faixa)</>
                  : feeType === "neighborhood" ? <> (bairro fora da lista)</> : null}.
              </span>
            </div>
          )}

          {richMode && feeInfo?.feePending && !allowFeeEdit && (
            <div className="text-xs text-muted-foreground" data-testid="pdv-delivery-fee-info">
              Taxa de entrega: <span className="font-semibold text-foreground">a calcular</span>
            </div>
          )}

          {richMode && !feeLoading && dynamicFee && dynamicFee.noDelivery && (
            <div className="p-2 rounded border border-red-200 bg-red-50 text-xs text-red-700 flex items-start gap-2" data-testid="pdv-delivery-blocked">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              <span>Não entregamos nesse endereço/distância. Selecione retirada no local.</span>
            </div>
          )}

          {richMode && !feeLoading && dynamicFee && !dynamicFee.noDelivery && dynamicFee.fee !== null && dynamicFee.fee > 0 && !allowFeeEdit && (
            <div className="text-xs text-muted-foreground" data-testid="pdv-delivery-fee-info">
              Taxa de entrega: <span className="font-semibold text-foreground">{formatCurrency(dynamicFee.fee)}</span>
              {dynamicFee.distanceKm != null && <> ({formatKmPt(dynamicFee.distanceKm)} km)</>}
            </div>
          )}

          {richMode && feeType === "fixed" && (finalDeliveryFee ?? 0) > 0 && !allowFeeEdit && (
            <div className="text-xs text-muted-foreground">
              Taxa de entrega: <span className="font-semibold text-foreground">{formatCurrency(finalDeliveryFee ?? 0)}</span>
            </div>
          )}

          {richMode && allowFeeEdit && (
            <div className="flex items-center justify-between gap-2 text-xs">
              {!editingFee ? (
                <>
                  <div className="text-muted-foreground" data-testid="pdv-delivery-fee-info">
                    Taxa de entrega:{" "}
                    <span className="font-semibold text-foreground">
                      {feeInfo?.feePending ? "a calcular" : freeDeliveryApplied ? "Grátis" : formatCurrency(finalDeliveryFee ?? 0)}
                    </span>
                    {feeOverride !== null && feeOverride !== undefined && (
                      <span className="ml-1 text-amber-600">(ajustada)</span>
                    )}
                    {dynamicFee?.distanceKm != null && <> ({formatKmPt(dynamicFee.distanceKm)} km)</>}
                  </div>
                  <div className="flex items-center gap-2">
                    {feeOverride !== null && feeOverride !== undefined && (
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground underline"
                        onClick={() => onFeeOverrideChange?.(null)}
                        data-testid="pdv-delivery-fee-reset"
                      >
                        Restaurar
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      onClick={() => {
                        setFeeDraft(((finalDeliveryFee ?? 0)).toFixed(2).replace(".", ","));
                        setEditingFee(true);
                      }}
                      data-testid="pdv-delivery-fee-edit"
                    >
                      <Pencil className="w-3 h-3" /> Editar
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 w-full">
                  <Label className="text-xs shrink-0">Taxa</Label>
                  <div className="flex-1">
                    <CurrencyInput
                      value={feeDraft}
                      onChange={(v) => setFeeDraft(v)}
                      placeholder="0,00"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      // `parseCurrency` (e não parse na mão): o CurrencyInput
                      // emite "10.00" com PONTO decimal, e o parse antigo tratava
                      // esse ponto como separador de milhar — digitar 10,00 virava
                      // R$ 1.000,00 e 0,10 virava R$ 10,00. Todos os outros campos
                      // de dinheiro do PDV já passam por aqui.
                      const n = parseCurrency(feeDraft);
                      const safe = Number.isFinite(n) && n >= 0 ? n : 0;
                      onFeeOverrideChange?.(safe);
                      setEditingFee(false);
                    }}
                    data-testid="pdv-delivery-fee-save"
                  >
                    OK
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingFee(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          )}

          {richMode && freeDeliveryApplied && (
            <div className="p-2 rounded border border-green-200 bg-green-50 text-xs text-green-700 flex items-start gap-2" data-testid="pdv-free-delivery">
              <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" />
              <span>Entrega grátis aplicada!</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SuggestiveSection({ products, cart, store, onPick }: { products: Product[]; cart: CartItem[]; store: any; onPick: (product: Product) => void }) {
  const items = useMemo(() => {
    const cartIds = new Set(cart.map((it) => it.product.id));
    return (products as any[]).filter((p) =>
      p.isSuggestive &&
      !cartIds.has(p.id) &&
      p.status === "ativo" &&
      (p.soldByWeight
        ? p.stockKg == null || parseFloat(String(p.stockKg)) > 0
        : p.stock === null || p.stock === undefined || p.stock > 0)
    );
  }, [products, cart]);
  if (items.length === 0) return null;
  const text = store?.suggestiveText || "Que tal adicionar também?";
  return (
    <div className="space-y-2 pt-3 border-t border-border" data-testid="pdv-suggestive-section">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-secondary" />
        <p className="text-xs font-semibold">{text}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {items.map((sp: any) => {
          const promo = getActivePromotion(sp, new Date(), "pdv");
          const aPartir = aPartirDe(sp);
          return (
            <button
              key={sp.id}
              onClick={() => onPick(sp)}
              className="bg-muted/30 border border-border rounded-lg p-2 flex flex-col items-stretch text-left hover:border-primary/40 hover:bg-muted/50 transition-colors"
              data-testid={`pdv-suggestive-${sp.id}`}
            >
              {sp.imageUrl && <img src={sp.imageUrl} alt="" className="w-full h-14 rounded object-cover mb-1.5" loading="lazy" />}
              <p className="text-xs font-bold leading-tight line-clamp-2">{sp.name}</p>
              {aPartir !== null ? (
                <p className="text-xs mt-1"><PrecoAPartirDe ap={aPartir} tamanho="sugestao" /></p>
              ) : promo.active ? (
                <div className="flex items-baseline gap-1 mt-1 flex-wrap">
                  <span className="text-xs font-bold text-primary">{formatCurrency(promo.finalPrice)}</span>
                  <span className="text-[10px] text-muted-foreground line-through">{formatCurrency(promo.originalPrice)}</span>
                </div>
              ) : (
                <p className="text-xs font-bold text-primary mt-1">{formatCurrency(promo.originalPrice)}</p>
              )}
              <span className="mt-1.5 text-[11px] bg-primary text-primary-foreground rounded py-1 font-semibold text-center">+ Adicionar</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MinOrderBanner({ minOrderValue, subtotal }: { minOrderValue: number; subtotal: number }) {
  if (!minOrderValue || minOrderValue <= 0) return null;
  if (subtotal >= minOrderValue) return null;
  const missing = minOrderValue - subtotal;
  return (
    <div className="flex items-start gap-2 text-[11px] leading-tight text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 rounded p-2" data-testid="pdv-min-order-banner">
      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <span>Faltam <strong>{formatCurrency(missing)}</strong> para o pedido mínimo de {formatCurrency(minOrderValue)}.</span>
    </div>
  );
}

export function DiscountSection({ discountValue, discountType, onValueChange, onTypeChange }: { discountValue: number; discountType: "fixed" | "percent"; onValueChange: (v: number) => void; onTypeChange: (t: "fixed" | "percent") => void }) {
  return (
    <div className="space-y-2 pt-2 border-t border-border">
      <Label className="text-xs">Desconto manual (opcional)</Label>
      <div className="flex gap-2">
        <button
          onClick={() => onTypeChange("fixed")}
          className={`flex-1 py-1.5 rounded text-xs border ${discountType === "fixed" ? "bg-primary/10 border-primary text-primary" : "border-border bg-card"}`}
          data-testid="pdv-discount-fixed"
        >
          R$
        </button>
        <button
          onClick={() => onTypeChange("percent")}
          className={`flex-1 py-1.5 rounded text-xs border ${discountType === "percent" ? "bg-primary/10 border-primary text-primary" : "border-border bg-card"}`}
          data-testid="pdv-discount-percent"
        >
          %
        </button>
      </div>
      {discountType === "fixed" ? (
        <CurrencyInput value={discountValue} onChange={(s) => onValueChange(parseCurrency(s))} placeholder="R$ 0,00" />
      ) : (
        <Input type="number" min={0} max={100} value={discountValue || ""} onChange={(e) => onValueChange(Number(e.target.value))} placeholder="0%" data-testid="pdv-discount-value-percent" />
      )}
    </div>
  );
}

export function PaymentSection({ acceptedPayments, paymentMethod, onPaymentMethodChange, paymentMixed, onPaymentMixedChange, paymentParts, onPaymentPartsChange, cashReceived, onCashReceivedChange, total, crediarioNames, crediarioInstallments, onCrediarioInstallmentsChange, crediarioFirstDue, onCrediarioFirstDueChange, crediarioInterest, crediarioTravado }: { acceptedPayments: string[]; paymentMethod: string; onPaymentMethodChange: (m: string) => void; paymentMixed: boolean; onPaymentMixedChange: (b: boolean) => void; paymentParts: PaymentPart[]; onPaymentPartsChange: (p: PaymentPart[]) => void; cashReceived: number; onCashReceivedChange: (v: number) => void; total: number; cashChange: number; crediarioNames?: string[]; crediarioInstallments?: number; onCrediarioInstallmentsChange?: (n: number) => void; crediarioFirstDue?: string; onCrediarioFirstDueChange?: (d: string) => void; crediarioInterest?: number; crediarioTravado?: boolean }) {
  const showCash = paymentMixed
    ? paymentParts.some((p) => p.method === "Dinheiro")
    : paymentMethod === "Dinheiro";
  const credNames = crediarioNames ?? [];
  const showCrediario =
    credNames.length > 0 &&
    !!onCrediarioInstallmentsChange &&
    (paymentMixed
      ? paymentParts.some((p) => credNames.includes(p.method))
      : credNames.includes(paymentMethod));

  return (
    <div className="space-y-2 pt-2 border-t border-border">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Pagamento</Label>
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={paymentMixed} onCheckedChange={onPaymentMixedChange} data-testid="pdv-payment-mixed-toggle" />
          Misto
        </label>
      </div>
      {!paymentMixed ? (
        <div className="grid grid-cols-2 gap-2">
          {acceptedPayments.map((m) => {
            const Icon = m === "Dinheiro" ? Banknote : m === "Pix" || m === "PIX" ? Wallet : CreditCard;
            return (
              <button
                key={m}
                onClick={() => onPaymentMethodChange(m)}
                className={`flex items-center justify-center gap-2 py-2 rounded border text-sm ${paymentMethod === m ? "bg-primary/10 border-primary text-primary" : "border-border bg-card"}`}
                data-testid={`pdv-payment-${m}`}
              >
                <Icon className="w-4 h-4" />{getPaymentMethodLabel(m)}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {paymentParts.map((p, i) => (
            <div key={i} className="flex gap-2 items-center">
              <select
                value={p.method}
                onChange={(e) => {
                  const next = [...paymentParts];
                  next[i] = { ...p, method: e.target.value };
                  onPaymentPartsChange(next);
                }}
                className="flex-1 text-sm px-2 py-1.5 border border-border rounded bg-background"
                data-testid={`pdv-payment-part-method-${i}`}
              >
                {acceptedPayments.map((m) => (<option key={m} value={m}>{getPaymentMethodLabel(m)}</option>))}
              </select>
              <CurrencyInput
                value={p.amount}
                onChange={(s) => {
                  const next = [...paymentParts];
                  next[i] = { ...p, amount: parseCurrency(s) };
                  onPaymentPartsChange(next);
                }}
                className="w-28"
              />
              <button onClick={() => onPaymentPartsChange(paymentParts.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive p-1" data-testid={`pdv-payment-part-remove-${i}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const used = paymentParts.reduce((s, p) => s + p.amount, 0);
              const remaining = Math.max(0, Math.round((total - used) * 100) / 100);
              onPaymentPartsChange([...paymentParts, { method: acceptedPayments[0] || "Dinheiro", amount: remaining }]);
            }}
            data-testid="pdv-payment-part-add"
          >
            <Plus className="w-3 h-3 mr-1" />Adicionar forma
          </Button>
        </div>
      )}
      {showCrediario && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div className="space-y-1">
            <Label className="text-xs">Parcelas</Label>
            <select
              value={crediarioInstallments ?? 2}
              onChange={(e) => onCrediarioInstallmentsChange?.(parseInt(e.target.value, 10) || 1)}
              disabled={crediarioTravado}
              className="w-full text-sm px-2 py-1.5 border border-border rounded bg-background h-9 disabled:opacity-60"
              data-testid="pdv-crediario-installments"
            >
              {Array.from({ length: 24 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n}x</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">1ª parcela</Label>
            <DataBr
              value={crediarioFirstDue ?? ""}
              onChange={(v) => onCrediarioFirstDueChange?.(v)}
              disabled={crediarioTravado}
              className="h-9 text-sm"
              data-testid="pdv-crediario-first-due"
            />
          </div>
          <p className="col-span-2 text-[11px] text-muted-foreground">
            {(crediarioInterest ?? 0) > 0 ? `Juros de ${crediarioInterest}% incluídos nas parcelas. ` : ""}
            {crediarioTravado
              ? "Parcelas já lançadas no Financeiro. Se o total mudar, a diferença vai para as que faltam receber."
              : "As parcelas viram contas a receber no Financeiro, com lembrete de vencimento."}
          </p>
        </div>
      )}
      {showCash && (
        <div className="space-y-1 pt-1">
          <Label className="text-xs">Valor recebido (Dinheiro)</Label>
          <CurrencyInput value={cashReceived} onChange={(s) => onCashReceivedChange(parseCurrency(s))} />
        </div>
      )}
    </div>
  );
}

type CapacidadeDoProdutoPDV = {
  slotExclusive?: boolean;
  slotIntervalMinutes?: number;
  bookedTimes?: Record<string, string[]>;
  bookedShifts?: Record<string, string[]>;
  orderShiftCapacity?: number | null;
  shiftBookedCounts?: Record<string, Record<string, number>>;
  dayExclusive?: boolean;
  dayLimit?: number | null;
  bookedDays?: Record<string, number>;
};

// Dia no limite de encomendas da loja: o PDV avisa e pede o de acordo (quem lança é a loja).
function avisoDoDiaCheio(ds: string, limite: number | null): string {
  return limite && limite > 1
    ? `O dia ${dataCurta(ds)} já tem ${limite} encomendas (o limite da loja).`
    : `Já existe encomenda em ${dataCurta(ds)} — e a loja está configurada para uma por dia.`;
}

// Data única no carrinho do PDV (05/10/2026): uma data e um horário para todos os itens por encomenda. Quem lança
// é a loja, então nada bloqueia: dia sem vaga/fora dos dias de um produto avisa, e horário antes do prazo ou já
// ocupado sai âmbar e pede "Colocar mesmo assim", como no diálogo do produto.
export function EncomendaDoCarrinhoPDV({ slug, itens, data, hora, turno = null, horarios = HORARIO_DOS_TURNOS_PADRAO, diasDoPedido = [], onChange }: {
  slug?: string;
  itens: ItemDeEncomenda[];
  data: string | null;
  hora: string | null;
  turno?: Turno | null;
  horarios?: HorarioDosTurnos;
  // Dias do pedido que o PDV está editando: não contam contra o limite de encomendas por dia.
  diasDoPedido?: string[];
  onChange: (data: string | null, hora: string | null, turno: Turno | null) => void;
}) {
  const [pendente, setPendente] = useState<{ t: string; motivo: string; turno?: Turno } | null>(null);
  // Dia no limite de encomendas (por dia): também não bloqueia, avisa e pede o de acordo.
  const [pendenteDia, setPendenteDia] = useState<string | null>(null);
  const qc = useQueryClient();
  const mes = (data ?? hojeNaLoja()).slice(0, 7);
  const ids = [...new Set(itens.map((i) => i.productId))].sort().join(",");
  const capDoMes = (m: string) => ({
    queryKey: ["pdv-order-capacity-cart", slug, ids, m],
    queryFn: () => customFetch<CapacidadeDoCarrinho>(`/api/catalog/${slug}/order-capacity-cart?ids=${ids}&month=${parseInt(m.slice(5), 10)}&year=${m.slice(0, 4)}`),
  });
  const { data: cap } = useQuery<CapacidadeDoCarrinho>({ ...capDoMes(mes), enabled: !!slug && itens.length > 0 });
  const escolherData = async (ds: string | null) => {
    setPendente(null);
    setPendenteDia(null);
    if (ds && limiteDoDia(cap)) {
      // A data pode cair noutro mês: busca a ocupação dele antes de decidir.
      const doMes = await qc.fetchQuery({ ...capDoMes(ds.slice(0, 7)), staleTime: 30_000 }).catch(() => null);
      if (diaOcupado(doMes, ds, diasDoPedido)) { setPendenteDia(ds); return; }
    }
    onChange(ds, null, null);
  };
  // Modos diferentes no carrinho (12/10/2026): vale o mais detalhado — horário, turno ou só a data.
  const modo = modoDoCarrinho(itens);
  const prazo = prazoDoCarrinho(itens);
  const motivoDia = data ? motivoDoDia(itens, data, cap ?? null, null, horarios, diasDoPedido) : null;
  const horariosDoDia = data && modo === "horario" ? horariosDoCarrinho(itens, data, cap ?? null, prazo, { semPrazo: true, comOcupados: true }, horarios) : [];
  const turnosDoDia = data && modo === "turno" ? turnosDoCarrinho(itens, data, cap ?? null, prazo, horarios, { semPrazo: true, comOcupados: true }) : [];
  const ocupados = new Set(data && cap?.slotExclusive ? cap.bookedTimes[data] ?? [] : []);
  const antesDoPrazo = (t: string) => !!prazo && !!data && (data < prazo.data || (data === prazo.data && !!prazo.hora && t < prazo.hora));
  const titulo = modo === "turno" ? "Data e turno da encomenda" : modo === "data" ? "Data da encomenda" : "Data e horário da encomenda";
  return (
    <div className="space-y-2 bg-orange-50 border border-orange-200 rounded p-3" data-testid="pdv-encomenda-carrinho">
      <Label className="font-semibold text-orange-800">{titulo}</Label>
      <p className="text-xs text-orange-700">
        Vale para o pedido todo.{prazo && (prazo.data > hojeNaLoja() || prazo.hora) ? ` Prazo: a partir de ${dataCurta(prazo.data)}${prazo.hora ? ` às ${prazo.hora}` : ""}.` : ""}
      </p>
      <DataBr
        value={data || ""}
        onChange={(v) => { void escolherData(v || null); }}
        data-testid="pdv-encomenda-data"
      />
      {pendenteDia && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2" data-testid="pdv-encomenda-dia-ocupado">
          <p className="text-xs text-amber-900 font-medium flex items-start gap-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {avisoDoDiaCheio(pendenteDia, limiteDoDia(cap))}
          </p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
              onClick={() => { onChange(pendenteDia, null, null); setPendenteDia(null); }}>
              Colocar mesmo assim
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPendenteDia(null)}>
              Escolher outro
            </Button>
          </div>
        </div>
      )}
      {data && motivoDia && motivoDia !== "antes do prazo" && (
        <p className="text-xs text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {motivoDia[0]!.toUpperCase() + motivoDia.slice(1)}.</p>
      )}
      {data && modo === "data" && (
        <p className="text-xs text-orange-700" data-testid="pdv-encomenda-so-data">Sem horário: o cliente combina com a loja.</p>
      )}
      {data && modo === "turno" && (
        <div className="flex flex-wrap gap-1">
          {turnosDoDia.length === 0 && <span className="text-xs text-orange-700">Nenhum turno em comum nesse dia.</span>}
          {turnosDoDia.map((t) => {
            const motivo = antesDoPrazo(horarios[t].start) ? "antes do prazo de antecedência" : turnoIndisponivel(itens, data, t, cap ?? null);
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  if (motivo && turno !== t) { setPendente({ t: NOME_DO_TURNO[t], motivo, turno: t }); return; }
                  setPendente(null);
                  onChange(data, null, t);
                }}
                className={`text-xs px-2 py-1 rounded border ${turno === t ? "bg-primary text-primary-foreground border-primary" : motivo ? "bg-amber-50 border-amber-300 text-amber-800 line-through" : "bg-card border-border"}`}
                data-testid={`pdv-encomenda-turno-${t}`}
              >
                {NOME_DO_TURNO[t]} · {faixaDoTurnoTexto(horarios[t])}
              </button>
            );
          })}
        </div>
      )}
      {data && modo === "horario" && (
        <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
          {horariosDoDia.length === 0 && <span className="text-xs text-orange-700">Nenhum horário em comum nesse dia.</span>}
          {horariosDoDia.map((t) => {
            const motivo = antesDoPrazo(t) ? "antes do prazo de antecedência" : ocupados.has(t) ? "já tem encomenda nesse horário e a loja está em uma por horário" : null;
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  if (motivo && hora !== t) { setPendente({ t, motivo }); return; }
                  setPendente(null);
                  onChange(data, t, null);
                }}
                className={`text-xs px-2 py-1 rounded border ${hora === t ? "bg-primary text-primary-foreground border-primary" : motivo ? "bg-amber-50 border-amber-300 text-amber-800 line-through" : "bg-card border-border"}`}
                data-testid={`pdv-encomenda-hora-${t}`}
              >
                {t}
              </button>
            );
          })}
        </div>
      )}
      {pendente && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2" data-testid="pdv-encomenda-confirmar">
          <p className="text-xs text-amber-900 font-medium flex items-start gap-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {pendente.t}: {pendente.motivo}.
          </p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
              onClick={() => {
                if (pendente.turno) onChange(data, null, pendente.turno);
                else onChange(data, pendente.t, null);
                setPendente(null);
              }}>
              Colocar mesmo assim
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPendente(null)}>
              Escolher outro
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProductDetailDialog({ product, editingItem, onClose, onConfirm, remainingKg, slug, dataUnica = false, horarios = HORARIO_DOS_TURNOS_PADRAO, diasDoPedido = [], canalDaPromo = "pdv" }: { product: Product; editingItem: CartItem | null; onClose: () => void; onConfirm: (qty: number, notes: string, opts: SelectedOption[], removed: string[], scheduledDate?: string, scheduledTime?: string, weightKg?: number, scheduledShift?: string) => void; remainingKg?: number | null; slug?: string; dataUnica?: boolean; horarios?: HorarioDosTurnos; diasDoPedido?: string[]; canalDaPromo?: PromoChannel }) {
  const p = product as any;
  // Com a data única da loja o produto por encomenda não pede data aqui: ela fica no carrinho, para o pedido todo.
  const isOrderBased = p.isOrderBased && !dataUnica;
  const encomendaNoCarrinho = !!p.isOrderBased && dataUnica;
  const soldByWeight = !!p.soldByWeight;
  // Pacote de escolhas (Pedro, 15/10/2026): a mesma conta do link; no PDV só avisa («só avisar»). Com a contagem, o grupo
  // não tem máximo próprio. A configuração só chega com o recurso (o servidor e a página do PDV tiram sem ele).
  const contandoPreparos = prepCountOn(p.prepCount);
  const gruposDoProduto: OptionGroup[] = groupsForValidation(p.optionGroups || [], contandoPreparos);
  // Promoção em % no produto de preço base 0: as opções que formam o preço já com o desconto (o servidor cobra assim).
  const groups: OptionGroup[] = gruposComPromocao({ ...p, optionGroups: gruposDoProduto }, canalDaPromo);
  const ingredients = p.removableIngredients || [];
  const availabilityCfg = p.availabilityConfig as ProductAvailabilityConfig | null;
  const minAdvance = p.minAdvanceDays || 0;
  const minAdvanceHours: number | null = p.minAdvanceHours || null;
  // «Aceitar encomendas» de/até (26/10/2026): no PDV só avisa (quem lança é a loja).
  const ateDoProduto: string | null = typeof p.orderUntilDate === "string" && p.orderUntilDate ? p.orderUntilDate.slice(0, 10) : null;
  const deDoProduto: string | null = typeof p.orderFromDate === "string" && p.orderFromDate ? p.orderFromDate.slice(0, 10) : null;
  // O que o produto pede (12/10/2026): horário, turno ou só a data.
  const regra: RegraDaEncomenda = { modo: modoDaEncomenda(p), turnos: turnosDoProduto(p), horarios };

  const [qty, setQty] = useState(editingItem?.quantity ?? 1);
  const [notes, setNotes] = useState(editingItem?.notes ?? "");
  // Peso em kg digitado pelo operador (aceita vírgula ou ponto; ex: "0,545").
  const [weightStr, setWeightStr] = useState(
    editingItem?.weightKg != null
      ? (unidadeMenor((product as any).weightUnit)
          ? String(Math.round(editingItem.weightKg * 1000))
          : String(editingItem.weightKg).replace(".", ","))
      : "",
  );
  // g e ml digitam em unidades (1 ml = 1 g = 0,001 kg; T-0056, 04/11/2026).
  const unidadeDoPeso = (product as any).weightUnit as string | undefined;
  const menor = unidadeMenor(unidadeDoPeso);
  const emGramas = !!menor;
  const weightKg = useMemo(() => {
    const bruto = parseFloat(weightStr.replace(",", "."));
    const w = emGramas ? bruto / 1000 : bruto;
    return Number.isFinite(w) && w > 0 && w <= 999 ? Math.round(w * 1000) / 1000 : null;
  }, [weightStr, emGramas]);
  // Indexado pela POSIÇÃO do grupo, não pelo nome: o lojista repete o mesmo
  // título em vários grupos e por nome as escolhas colapsavam num balde só.
  const [selections, setSelections] = useState<Record<string, string[]>>(() =>
    editingItem ? selectionsByGroupIndex(groups, editingItem.selectedOptions) : {},
  );
  const [removed, setRemoved] = useState<string[]>(editingItem?.removedIngredients ?? []);
  const [scheduledDate, setScheduledDate] = useState<string | undefined>(editingItem?.scheduledDate);
  const [scheduledTime, setScheduledTime] = useState<string | undefined>(editingItem?.scheduledTime);
  const [scheduledShift, setScheduledShift] = useState<Turno | undefined>(editingItem?.scheduledShift as Turno | undefined);
  // Turno antes do prazo, já reservado (uma por turno) ou no limite do produto: o PDV avisa e pede o de acordo.
  const [pendingTurno, setPendingTurno] = useState<{ t: Turno; aviso: string } | null>(null);
  // Horário ocupado aguardando o "de acordo" do lojista (loja com horário
  // exclusivo ligado): o PDV NÃO bloqueia, mas avisa e pede confirmação.
  const [pendingTakenTime, setPendingTakenTime] = useState<string | null>(null);
  // Antecedência em horas: horário antes do prazo também NÃO bloqueia no PDV (quem lança é a loja), avisa e
  // pede o de acordo, como o horário ocupado.
  const [pendingEarlyTime, setPendingEarlyTime] = useState<string | null>(null);
  // Dia no limite de encomendas da loja: o mesmo aviso com o de acordo.
  const [pendingDia, setPendingDia] = useState<string | null>(null);
  const qc = useQueryClient();

  // Ocupação/intervalo da loja — mesmo endpoint público do link. Mês segue a
  // data escolhida (a query refaz ao navegar de mês).
  const capMonth = scheduledDate ? scheduledDate.slice(0, 7) : new Date().toISOString().slice(0, 7);
  const capDoMes = (m: string) => ({
    queryKey: ["pdv-order-capacity", slug, p.id, m],
    queryFn: () => customFetch<CapacidadeDoProdutoPDV>(`/api/catalog/${slug}/order-capacity?productId=${p.id}&month=${parseInt(m.slice(5), 10)}&year=${m.slice(0, 4)}`),
  });
  const { data: slotData } = useQuery<CapacidadeDoProdutoPDV>({ ...capDoMes(capMonth), enabled: !!slug && !!isOrderBased });
  const slotStep = slotData?.slotIntervalMinutes ?? 30;
  const takenTimes = useMemo(() => {
    if (!slotData?.slotExclusive || !scheduledDate) return new Set<string>();
    const set = new Set(slotData.bookedTimes?.[scheduledDate] ?? []);
    // Editando um item já agendado, o horário DELE não conta como conflito.
    if (editingItem?.scheduledDate === scheduledDate && editingItem?.scheduledTime) set.delete(editingItem.scheduledTime);
    return set;
  }, [slotData, scheduledDate, editingItem]);

  const selectedOptions: SelectedOption[] = useMemo(() => {
    const res: SelectedOption[] = [];
    groups.forEach((g: any, gi: number) => {
      for (const sel of selections[optionGroupKey(gi)] || []) {
        const opt = g.options?.find((o: any) => o.name === sel);
        if (opt) res.push({ groupName: g.name, optionName: opt.name, price: opt.price || 0, groupIndex: gi });
      }
    });
    return res;
  }, [selections, groups]);

  const promo = getActivePromotion(product, new Date(), "pdv");
  const pricePerUnitOrKg = promo.active ? promo.finalPrice : promo.originalPrice;
  // Por peso: o "unitário" da linha é peso × preço/kg (calculado na hora).
  const unitPrice = soldByWeight
    ? Math.round(pricePerUnitOrKg * (weightKg ?? 0) * 100) / 100
    : pricePerUnitOrKg;
  const preparos = contandoPreparos ? prepCountDoItem(p.prepCount, groups, selectedOptions) : null;
  // Os extras entram no valor das opções da unidade, como no link e no servidor.
  const optionsTotal = computeOptionsTotal(groups, selectedOptions) + (preparos?.extrasValue ?? 0);
  const lineTotal = (unitPrice + optionsTotal) * qty;

  const nextDates = useMemo(() => {
    if (!isOrderBased || !isAvailabilityConfigActive(availabilityCfg)) return [];
    return proximasDatasEncomenda(availabilityCfg!, minAdvance, minAdvanceHours, 14, new Date(), 60, regra, ateDoProduto, deDoProduto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityCfg, isOrderBased, minAdvance, minAdvanceHours, regra.modo, regra.turnos.join(","), ateDoProduto, deDoProduto]);
  const limparAgenda = () => { setScheduledTime(undefined); setScheduledShift(undefined); setPendingTakenTime(null); setPendingEarlyTime(null); setPendingTurno(null); };
  // Limite por dia: a ocupação vem do mês e dos próximos 60 dias (cobre as datas sugeridas); data de outro mês
  // busca o dela antes de decidir.
  const escolherData = async (d: string) => {
    setPendingDia(null);
    let cap = slotData;
    if (limiteDoDia(slotData) && d.slice(0, 7) !== capMonth) cap = await qc.fetchQuery({ ...capDoMes(d.slice(0, 7)), staleTime: 30_000 }).catch(() => slotData);
    if (d !== scheduledDate && diaOcupado(cap, d, diasDoPedido)) { setPendingDia(d); return; }
    setScheduledDate(d);
    limparAgenda();
  };
  const primeiro = minAdvanceHours ? primeiroMomentoEncomenda(minAdvanceHours) : null;
  const cedoDemais = (t: string) =>
    !!primeiro && !!scheduledDate && (scheduledDate < primeiro.data || (scheduledDate === primeiro.data && t < primeiro.hora));
  const turnosDaData = scheduledDate && regra.modo === "turno" ? turnosDoDia(availabilityCfg, scheduledDate, regra, null) : [];
  const avisoDoTurno = (t: Turno): string | null => {
    if (!scheduledDate) return null;
    if (cedoDemais(regra.horarios[t].start)) return `antes do prazo de ${minAdvanceHours} horas deste produto`;
    const proprio = editingItem?.scheduledDate === scheduledDate && editingItem?.scheduledShift === t;
    if (!proprio && slotData?.slotExclusive && (slotData.bookedShifts?.[scheduledDate] ?? []).includes(t)) {
      return "já tem encomenda nesse turno e a loja está em uma por horário";
    }
    const limite = slotData?.orderShiftCapacity;
    if (!proprio && limite && (slotData?.shiftBookedCounts?.[scheduledDate]?.[t] ?? 0) + qty > limite) {
      return `passa do limite de ${limite} por turno deste produto`;
    }
    return null;
  };

  const allowedTimes = useMemo(() => {
    if (!scheduledDate || !isAvailabilityConfigActive(availabilityCfg)) {
      const slots: string[] = [];
      for (let t = 8 * 60; t <= 20 * 60; t += slotStep > 0 ? slotStep : 30) {
        slots.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
      }
      return slots;
    }
    return getAvailableTimeSlotsForDate(availabilityCfg!, scheduledDate, slotStep);
  }, [scheduledDate, availabilityCfg, slotStep]);

  const isValidGroupSelection = useMemo(
    () => isValidGroupSelectionShared(groups, selections),
    [selections, groups],
  );

  // Peso pedido acima do saldo (carrinho já descontado) trava o confirmar.
  const kgOverStock =
    soldByWeight && remainingKg != null && weightKg !== null
      ? Math.round(weightKg * qty * 1000) / 1000 > remainingKg
      : false;

  const agendaOk = !!scheduledDate && (regra.modo === "horario" ? !!scheduledTime : regra.modo === "turno" ? !!scheduledShift : true);
  const canConfirm = qty > 0 && isValidGroupSelection && (!isOrderBased || agendaOk) && (!soldByWeight || (weightKg !== null && !kgOverStock));

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(
      qty,
      notes,
      selectedOptions,
      removed,
      isOrderBased ? scheduledDate : undefined,
      isOrderBased && regra.modo === "horario" ? scheduledTime : undefined,
      soldByWeight ? weightKg ?? undefined : undefined,
      isOrderBased && regra.modo === "turno" ? scheduledShift : undefined,
    );
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{p.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
          {soldByWeight ? (
            <div className="space-y-2">
              <div className="text-2xl font-bold text-primary">{precoPeso(pricePerUnitOrKg, unidadeDoPeso).valor}<span className="text-sm font-semibold text-muted-foreground">{precoPeso(pricePerUnitOrKg, unidadeDoPeso).sufixo}</span></div>
              <Label className="font-semibold">{menor === "ml" ? "Quantidade" : "Peso"} ({menor ?? "kg"})</Label>
              {parsePorcoes((product as any).portions).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {parsePorcoes((product as any).portions).map((o) => (
                    <button
                      key={o.kg}
                      type="button"
                      onClick={() => setWeightStr(emGramas ? String(Math.round(o.kg * 1000)) : String(o.kg).replace(".", ","))}
                      className="text-xs font-semibold border border-primary/40 text-primary rounded-full px-2.5 py-1 hover:bg-primary/10"
                      data-testid={`pdv-portion-${o.kg}`}
                    >
                      {pesoLabel(o.kg, unidadeDoPeso)}
                    </button>
                  ))}
                </div>
              )}
              <Input
                value={weightStr}
                onChange={(e) => setWeightStr(e.target.value.replace(/[^\d.,]/g, ""))}
                placeholder={menor === "ml" ? "Ex: 5" : emGramas ? "Ex: 545" : "Ex: 0,545"}
                inputMode="decimal"
                autoFocus
                data-testid="pdv-weight-input"
              />
              {weightKg !== null ? (
                <p className="text-sm">
                  {menor ? `${Math.round(weightKg * 1000)} ${menor}` : `${String(weightKg.toFixed(3)).replace(".", ",")} kg`} × {precoPeso(pricePerUnitOrKg, unidadeDoPeso).valor}{precoPeso(pricePerUnitOrKg, unidadeDoPeso).sufixo} ={" "}
                  <span className="font-bold text-primary">{formatCurrency(unitPrice)}</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">Digite o peso da balança pra calcular o valor.</p>
              )}
              {kgOverStock && remainingKg != null && (
                <p className="text-xs font-semibold text-destructive">
                  Estoque insuficiente — {remainingKg > 0 ? `restam ${formatKgPt(remainingKg)} kg` : "sem saldo em estoque"}.
                </p>
              )}
            </div>
          ) : (
            <div className="text-2xl font-bold text-primary">
              {(() => {
                const ap = aPartirDeComPromocao(product, canalDaPromo, gruposDoProduto);
                if (!ap) return formatCurrency(unitPrice);
                return (
                  <>
                    <span className="text-sm font-medium text-muted-foreground">A partir de </span>{formatCurrency(ap.valor)}
                    {ap.original != null && <span className="ml-2 text-base font-normal text-muted-foreground line-through">{formatCurrency(ap.original)}</span>}
                  </>
                );
              })()}
            </div>
          )}

          {/* «Cada lista conta separado» (28/10/2026): o contador de cada lista fica dentro dela. */}
          {preparos && !preparos.porLista && (
            <div className="sticky top-0 z-10 bg-background pb-1" data-testid="pdv-prep-counter">
              <ContadorDePreparos c={preparos} pdv />
            </div>
          )}
          {groups.map((g: any, gi: number) => {
            // Fonte de verdade: selectionType definido no admin (Products.tsx).
            // "multiple" => checkbox com limite opcional maxSelections.
            // "single"   => radio (uma única opção).
            const isMulti = g.selectionType === "multiple";
            const gKey = optionGroupKey(gi);
            const sels = selections[gKey] || [];
            const limit = isMulti && g.maxSelections ? Number(g.maxSelections) : null;
            return (
              <div key={gi} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">{g.name}</Label>
                  {g.required && <span className="text-xs text-destructive">Obrigatório</span>}
                </div>
                {(() => {
                  const lc = prepCountDaLista(preparos, gi);
                  return lc ? <div data-testid={`pdv-prep-lista-${gi}`}><ContadorDePreparos c={lc} pdv /></div> : null;
                })()}
                {isMulti && (() => {
                  // O piso do grupo também vale no PDV: sem ele o botão
                  // Confirmar ficava desabilitado sem dizer o que faltava.
                  // Com peso nas opções (Adicionais fracionados) o piso e o teto contam o peso: «Escolha até 4 · 3½ de 4».
                  const min = groupMinSelections(g as OptionGroup);
                  const comPeso = groupHasWeights(g as OptionGroup);
                  const soma = groupWeightedCount(g as OptionGroup, sels);
                  const faltam = min > 1 ? Math.max(0, min - soma) : 0;
                  const cls = `text-xs ${faltam > 0 ? "text-destructive font-semibold" : "text-muted-foreground"}`;
                  const cauda = faltam > 0 ? ` · ${faltam <= 1 ? "falta" : "faltam"} ${formatPreparos(faltam)}` : "";
                  if (limit) {
                    return (
                      <p className={cls} data-testid={`pdv-group-count-${gi}`}>
                        {min > 1 ? `Escolha ${min}` : `Escolha até ${limit}`}
                        {comPeso ? ` · ${formatPreparos(soma)} de ${limit}` : ` — ${sels.length}/${limit} selecionados`}{cauda}
                      </p>
                    );
                  }
                  if (min > 1) {
                    return (
                      <p className={cls}>
                        Escolha pelo menos {min} — {sels.length} {sels.length === 1 ? "selecionado" : "selecionados"}{cauda}
                      </p>
                    );
                  }
                  if (prepCountDaLista(preparos, gi)) return null;
                  return <p className="text-xs text-muted-foreground">Escolha quantas quiser</p>;
                })()}
                <div className="space-y-1">
                  {g.options?.map((opt: any) => {
                    const optCount = sels.filter((n) => n === opt.name).length;
                    const checked = optCount > 0;
                    // Adicionais fracionados: a opção conta o peso dela no teto do grupo (sem peso, é a quantidade).
                    const peso = optionWeight(g as OptionGroup, opt.name);
                    const atLimit = !!(limit && groupWeightedCount(g as OptionGroup, sels) + peso > limit + 1e-9);
                    const rotuloPeso = (contandoPreparos && optionInPackage(g as OptionGroup, opt.name)) || (isMulti && (!!limit || groupMinSelections(g as OptionGroup) > 1)) ? optionWeightLabel(peso) : null;
                    const seloPeso = rotuloPeso ? (
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap" data-testid="pdv-option-weight">{rotuloPeso}</span>
                    ) : null;

                    if (!isMulti) {
                      return (
                        <label
                          key={opt.name}
                          className="flex items-center gap-2 px-2 py-1.5 border border-border rounded cursor-pointer hover:bg-muted"
                        >
                          <input
                            type="radio"
                            name={`pdv-group-${gi}`}
                            checked={checked}
                            onChange={() => setSelections((prev) => ({ ...prev, [gKey]: [opt.name] }))}
                            // Rádio marcado não dispara onChange: na lista que não é obrigatória, o clique na marcada
                            // desmarca (como na vitrine); na obrigatória, só troca.
                            onClick={() => { if (checked && groupMinSelections(g as OptionGroup) === 0) setSelections((prev) => ({ ...prev, [gKey]: [] })); }}
                            data-testid={`pdv-option-${g.name}-${opt.name}`}
                          />
                          <span className="flex-1 text-sm">{opt.name}</span>
                          {seloPeso}
                          {opt.price > 0 && <span className="text-xs text-muted-foreground">+ {formatCurrency(opt.price)}</span>}
                        </label>
                      );
                    }

                    // "Permitir apenas 1 de cada": já selecionada → o "+" trava
                    // (o "−" continua disponível pra desmarcar). Espelha a regra
                    // do storefront; o server valida em validateOptionGroupCardinality.
                    const canIncrement = !atLimit && !(g.onePerOption && checked);
                    const handleIncrement = () => {
                      setSelections((prev) => {
                        const cur = prev[gKey] || [];
                        if (limit && groupWeightedCount(g as OptionGroup, [...cur, opt.name]) > limit + 1e-9) return prev;
                        return { ...prev, [gKey]: [...cur, opt.name] };
                      });
                    };
                    const handleDecrement = () => {
                      setSelections((prev) => {
                        const cur = prev[gKey] || [];
                        const idx = cur.lastIndexOf(opt.name);
                        if (idx === -1) return prev;
                        const updated = [...cur];
                        updated.splice(idx, 1);
                        return { ...prev, [gKey]: updated };
                      });
                    };

                    return (
                      <div
                        key={opt.name}
                        className={`flex items-center gap-2 px-2 py-1.5 border rounded ${checked ? "border-primary bg-primary/5" : "border-border"}`}
                        data-testid={`pdv-option-${g.name}-${opt.name}`}
                      >
                        <span className="flex-1 text-sm">{opt.name}</span>
                        {seloPeso}
                        {opt.price > 0 && <span className="text-xs text-muted-foreground">+ {formatCurrency(opt.price)}</span>}
                        <div className="flex items-center gap-1 shrink-0">
                          {optCount > 0 && (
                            <>
                              <button
                                type="button"
                                onClick={handleDecrement}
                                className="w-7 h-7 rounded bg-muted hover:bg-muted/80 flex items-center justify-center"
                                data-testid={`pdv-option-minus-${g.name}-${opt.name}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-6 text-center text-sm font-bold">{optCount}</span>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={handleIncrement}
                            disabled={!canIncrement}
                            className="w-7 h-7 rounded bg-primary/15 hover:bg-primary/25 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`pdv-option-plus-${g.name}-${opt.name}`}
                          >
                            <Plus className="w-3 h-3 text-primary" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {ingredients.length > 0 && (
            <div className="space-y-2">
              <Label className="font-semibold">Remover ingredientes</Label>
              <div className="flex flex-wrap gap-2">
                {ingredients.map((ing: string) => {
                  const isRemoved = removed.includes(ing);
                  return (
                    <button
                      key={ing}
                      onClick={() => setRemoved((prev) => isRemoved ? prev.filter((i) => i !== ing) : [...prev, ing])}
                      className={`text-xs px-2 py-1 rounded border ${isRemoved ? "bg-destructive/10 border-destructive text-destructive line-through" : "bg-card border-border"}`}
                      data-testid={`pdv-ingredient-${ing}`}
                    >
                      {ing}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {encomendaNoCarrinho && (
            <div className="text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded p-3" data-testid="pdv-encomenda-no-carrinho">
              Encomenda: a data e o horário ficam no carrinho, para o pedido todo.
            </div>
          )}
          {isOrderBased && (
            <div className="space-y-2 bg-orange-50 border border-orange-200 rounded p-3">
              <Label className="font-semibold text-orange-800">Encomenda — {regra.modo === "turno" ? "data e turno" : regra.modo === "data" ? "data" : "data e horário"}</Label>
              {nextDates.length > 0 ? (
                <div className="space-y-1">
                  <div className="text-xs text-orange-700">Próximas datas disponíveis:</div>
                  <div className="flex flex-wrap gap-1">
                    {nextDates.slice(0, 7).map((d) => (
                      <button
                        key={d}
                        onClick={() => { void escolherData(d); }}
                        className={`text-xs px-2 py-1 rounded border ${scheduledDate === d ? "bg-primary text-primary-foreground border-primary" : diaOcupado(slotData, d, diasDoPedido) ? "bg-amber-50 border-amber-300 text-amber-800 line-through" : "bg-card border-border"}`}
                        data-testid={`pdv-schedule-date-${d}`}
                      >
                        {formatAvailabilityDateLabel(d)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <DataBr
                  value={scheduledDate || ""}
                  onChange={(v) => {
                    if (v) { void escolherData(v); return; }
                    setPendingDia(null);
                    setScheduledDate(undefined);
                    limparAgenda();
                  }}
                  data-testid="pdv-schedule-date-input"
                />
              )}
              {scheduledDate && ((ateDoProduto && scheduledDate > ateDoProduto) || (deDoProduto && scheduledDate < deDoProduto)) && (
                <p className="text-xs text-amber-800 flex items-center gap-1" data-testid="pdv-schedule-depois-do-limite">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {deDoProduto && scheduledDate < deDoProduto
                    ? `Este produto aceita encomendas a partir de ${dataCurta(deDoProduto)}.`
                    : `Este produto aceita encomendas até ${dataCurta(ateDoProduto!)}.`}
                </p>
              )}
              {pendingDia && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2" data-testid="pdv-schedule-day-confirm">
                  <p className="text-xs text-amber-900 font-medium flex items-start gap-1.5">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    {avisoDoDiaCheio(pendingDia, limiteDoDia(slotData))}
                  </p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
                      onClick={() => { setScheduledDate(pendingDia); setPendingDia(null); limparAgenda(); }}>
                      Colocar mesmo assim
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPendingDia(null)}>
                      Escolher outro
                    </Button>
                  </div>
                </div>
              )}
              {scheduledDate && regra.modo === "turno" && (
                <div className="space-y-1">
                  <div className="text-xs text-orange-700">Turno:</div>
                  {turnosDaData.length === 0 ? (
                    <div className="text-xs text-orange-700">O produto não é feito em nenhum turno nesse dia.</div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {turnosDaData.map((t) => {
                        const aviso = avisoDoTurno(t);
                        return (
                          <button
                            key={t}
                            onClick={() => {
                              if (aviso && scheduledShift !== t) { setPendingTurno({ t, aviso }); return; }
                              setPendingTurno(null);
                              setScheduledShift(t);
                            }}
                            className={`text-xs px-2 py-1 rounded border ${scheduledShift === t ? "bg-primary text-primary-foreground border-primary" : aviso ? "bg-amber-50 border-amber-300 text-amber-800 line-through" : "bg-card border-border"}`}
                            data-testid={`pdv-schedule-shift-${t}`}
                          >
                            {NOME_DO_TURNO[t]} · {faixaDoTurnoTexto(regra.horarios[t])}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {pendingTurno && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2" data-testid="pdv-shift-confirm">
                      <p className="text-xs text-amber-900 font-medium flex items-start gap-1.5">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        {NOME_DO_TURNO[pendingTurno.t]}: {pendingTurno.aviso}.
                      </p>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
                          onClick={() => { setScheduledShift(pendingTurno.t); setPendingTurno(null); }}>
                          Colocar mesmo assim
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPendingTurno(null)}>
                          Escolher outro
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {scheduledDate && regra.modo === "data" && (
                <div className="text-xs text-orange-700" data-testid="pdv-schedule-so-data">Sem horário: o cliente combina com a loja.</div>
              )}
              {scheduledDate && regra.modo === "horario" && (
                <div className="space-y-1">
                  <div className="text-xs text-orange-700">Horário:</div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {allowedTimes.map((t) => {
                      const taken = takenTimes.has(t);
                      const early = cedoDemais(t);
                      return (
                        <button
                          key={t}
                          onClick={() => {
                            if (early && scheduledTime !== t) { setPendingEarlyTime(t); setPendingTakenTime(null); return; }
                            if (taken && scheduledTime !== t) { setPendingTakenTime(t); setPendingEarlyTime(null); return; }
                            setPendingTakenTime(null);
                            setPendingEarlyTime(null);
                            setScheduledTime(t);
                          }}
                          className={`text-xs px-2 py-1 rounded border ${scheduledTime === t ? "bg-primary text-primary-foreground border-primary" : taken || early ? "bg-amber-50 border-amber-300 text-amber-800 line-through" : "bg-card border-border"}`}
                          data-testid={`pdv-schedule-time-${t}`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  {pendingEarlyTime && primeiro && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2" data-testid="pdv-slot-early-confirm">
                      <p className="text-xs text-amber-900 font-medium flex items-start gap-1.5">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        Antes do prazo de {minAdvanceHours} horas deste produto: o primeiro horário é {primeiro.data.slice(8, 10)}/{primeiro.data.slice(5, 7)} às {primeiro.hora}.
                      </p>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
                          onClick={() => { setScheduledTime(pendingEarlyTime); setPendingEarlyTime(null); }}>
                          Colocar mesmo assim
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPendingEarlyTime(null)}>
                          Escolher outro
                        </Button>
                      </div>
                    </div>
                  )}
                  {pendingTakenTime && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2" data-testid="pdv-slot-taken-confirm">
                      <p className="text-xs text-amber-900 font-medium flex items-start gap-1.5">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        Já existe encomenda às {pendingTakenTime} nesse dia — e a loja está configurada para uma por horário.
                      </p>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
                          onClick={() => { setScheduledTime(pendingTakenTime); setPendingTakenTime(null); }}>
                          Colocar mesmo assim
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPendingTakenTime(null)}>
                          Escolher outro
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Observações</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Ex: sem cebola..."
              className="w-full text-sm px-3 py-2 border border-border rounded bg-background"
              data-testid="pdv-detail-notes"
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-1 border border-border rounded">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 hover:bg-muted" data-testid="pdv-detail-qty-minus">
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-3 font-semibold">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2 hover:bg-muted" data-testid="pdv-detail-qty-plus">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <span className="text-lg font-bold">{formatCurrency(lineTotal)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={!canConfirm} data-testid="pdv-detail-confirm">
            {editingItem ? "Atualizar" : "Adicionar ao pedido"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SuccessDialog({ success, onPrint, onWhatsApp, onNewSale, printerConnected }: { success: { orderNumber?: string; message: string; title?: string }; onPrint: () => void; onWhatsApp: () => void; onNewSale: () => void; printerConnected: boolean }) {
  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onNewSale(); }}>
      <DialogContent className="max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-green-600" /> {success.title || "Pedido registrado!"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {success.message}
            {success.orderNumber && <div className="mt-1">Número: <span className="font-mono font-semibold">{success.orderNumber}</span></div>}
          </div>
          <div className="space-y-2">
            {/* O Button não quebra linha (whitespace-nowrap); com o aviso na
                mesma linha ele estourava a largura do diálogo no celular. */}
            <Button onClick={onPrint} variant="outline" className="w-full min-w-0 h-auto min-h-9 py-2" disabled={!printerConnected} data-testid="pdv-success-print">
              <Printer className="w-4 h-4 mr-2" />
              <span className="flex flex-col items-start leading-tight">
                <span>Imprimir pedido</span>
                {!printerConnected && <span className="text-xs font-normal">Impressora desconectada</span>}
              </span>
            </Button>
            {/* Sempre ativo: sem número de verdade abre o WhatsApp sem destinatário (💡 #87). */}
            <Button onClick={onWhatsApp} variant="outline" className="w-full" data-testid="pdv-success-whats">
              <MessageCircle className="w-4 h-4 mr-2" />Enviar comprovante via WhatsApp
            </Button>
            <Button onClick={onNewSale} className="w-full" data-testid="pdv-success-new">
              <Plus className="w-4 h-4 mr-2" />Novo pedido
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Pedido do link alterado: mostra o texto e deixa o lojista decidir, como as
// mensagens de status em Pedidos. Enviar abre o WhatsApp; o envio é dele.
export function NotifyCustomerDialog({ message, onMessageChange, onSend, onSkip }: { message: string; onMessageChange: (m: string) => void; onSend: () => void; onSkip: () => void }) {
  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onSkip(); }}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" /> Avisar o cliente da alteração?
          </DialogTitle>
        </DialogHeader>
        <textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          rows={8}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none [field-sizing:content]"
          data-testid="pdv-notify-text"
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={onSend} className="flex-1" style={{ backgroundColor: "#25D366" }} data-testid="pdv-notify-send">
            <MessageCircle className="w-4 h-4 mr-2" />Enviar no WhatsApp
          </Button>
          <Button variant="outline" onClick={onSkip} className="flex-1" data-testid="pdv-notify-skip">
            Não avisar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function buildFullOrderForReceipt(payload: any) {
  const order = payload.order;
  const cart: CartItem[] = payload.items;
  const customerSel: CustomerSelection = payload.customer;
  let customerName = "Cliente avulso";
  let customerWhats = "";
  if (customerSel.kind === "existing") {
    customerName = customerSel.customer.firstName;
    customerWhats = customerSel.customer.whatsappNumber || "";
  } else if (customerSel.kind === "new") {
    customerName = customerSel.name;
    customerWhats = customerSel.whatsapp.replace(/\D/g, "");
  }
  // O POST /api/orders retorna apenas { id, total }. Preenchemos os demais
  // campos a partir do payload local (deliveryMode, address, paymentMethod
  // etc) para que o cupom impresso no PDV saia identico ao do reprint via
  // /Pedidos. Sem isso, deliveryMode cai no fallback "retirada" mesmo quando
  // o pedido eh entrega.
  const isDelivery = payload.deliveryMode === "entrega";
  const addr = payload.address || null;
  const fullAddress = isDelivery && addr
    ? {
        street: addr.street || "",
        number: addr.number || "",
        complement: addr.complement || "",
        neighborhood: addr.neighborhood || "",
        city: addr.city || "",
        state: addr.state || "",
        zipCode: addr.zipCode || "",
        reference: addr.reference || "",
      }
    : null;
  const premios: Array<{ name: string; type: string; points: number; discount: number }> = Array.isArray(payload.loyaltyRewards)
    ? payload.loyaltyRewards
    : [];
  return {
    ...order,
    createdAt: order?.createdAt || new Date().toISOString(),
    loyaltyDiscount: payload.loyaltyDiscount > 0 ? payload.loyaltyDiscount : null,
    loyaltyRewards: premios,
    items: [...cart.map((it) => ({
      productName: (it.product as any).name,
      quantity: it.quantity,
      price: String(it.unitPrice),
      optionsTotal: String(it.optionsTotal),
      selectedOptions: it.selectedOptions,
      removedIngredients: it.removedIngredients,
      scheduledDate: it.scheduledDate || null,
      scheduledTime: it.scheduledTime || null,
      scheduledShift: it.scheduledShift || null,
      notes: it.notes || null,
      weightKg: it.weightKg ?? null,
      // Pacote de escolhas: o cupom desta via (impresso pelo aparelho) leva a mesma linha do pedido gravado.
      prepCount: prepDoItem(it),
    })),
    // Brinde de pontos: linha de R$ 0,00, igual ao pedido gravado.
    ...premios
      .filter((p) => p.type === "gift")
      .map((p) => ({ productName: `Brinde: ${p.name}`, quantity: 1, price: "0", optionsTotal: "0", selectedOptions: [], removedIngredients: [], notes: null })),
    ],
    customer: { firstName: customerName, whatsappNumber: customerWhats },
    deliveryMode: payload.deliveryMode,
    address: fullAddress,
    paymentMethod: payload.payLater ? "Pendente" : (payload.paymentMixed ? "Misto" : payload.paymentMethod),
    paymentParts: payload.paymentMixed && Array.isArray(payload.paymentParts) && payload.paymentParts.length > 0 ? payload.paymentParts : null,
    deliveryFee: String(isDelivery ? (payload.deliveryFee || 0) : 0),
    // Frete a calcular: o cupom imprime «Entrega: a calcular», como o do app e o do painel (antes saía «Gratis»).
    deliveryFeePending: isDelivery && payload.deliveryFeePending === true,
    cashReceived: payload.cashReceived > 0 ? payload.cashReceived : null,
    cardSurcharge: payload.surcharge,
    needsChange: payload.cashChange > 0,
    changeFor: payload.cashReceived > 0 ? payload.cashReceived : null,
    notes: payload.orderNotes || order?.notes || null,
    tableNumber: payload.tableNumber || order?.tableNumber || null,
  };
}

// Pacote de escolhas: o retrato da conta do item do carrinho do PDV (null = o produto não conta, ou a loja sem o recurso).
function prepDoItem(it: CartItem) {
  const prep = prepCountFor((it.product as any)?.optionGroups, (it.product as any)?.prepCount, it.selectedOptions);
  return prep ? prepCountSnapshot(prep) : null;
}

export function buildWhatsAppMessage(payload: any): string {
  const store = (payload.store as any) || {};
  const items = (payload.items || []) as CartItem[];

  const premios: Array<{ name: string; type: string; points: number; discount: number }> = Array.isArray(payload.loyaltyRewards)
    ? payload.loyaltyRewards
    : [];
  const itemLines: string[] = [];
  for (const it of items) {
    const lt = (it.unitPrice + it.optionsTotal) * it.quantity;
    const weightStr = it.weightKg != null ? ` (${(it.product as any).weightUnit === "ml" ? formatarPeso(it.weightKg, "ml") : `${String(it.weightKg.toFixed(3)).replace(".", ",")} kg`})` : "";
    itemLines.push(`• ${it.quantity}x ${(it.product as any).name}${weightStr} - ${formatCurrency(lt)}`);
    const prep = prepDoItem(it);
    if (prep?.listas) {
      // «Cada lista conta separado»: cada adicional embaixo da sua lista, com a conta dela.
      for (const g of gruposComConta(it.selectedOptions, prep)) {
        if (g.titulo) itemLines.push(`   *${g.titulo}:*${g.conta ? ` ${g.conta}` : ""}`);
        if (g.opcoes.length > 0) itemLines.push(`   _${g.opcoes.map((o) => (o.count > 1 ? `${o.count}x ${o.nome}` : o.nome)).join(", ")}_`);
      }
    } else {
      if (prep) for (const l of prepCountLines(prep)) itemLines.push(`   _${l}_`);
      if (it.selectedOptions.length > 0) {
        itemLines.push(`   _${it.selectedOptions.map((o) => o.optionName).join(", ")}_`);
      }
    }
    if (it.removedIngredients && it.removedIngredients.length > 0) {
      itemLines.push(`   _Sem: ${it.removedIngredients.join(", ")}_`);
    }
    if (it.notes) itemLines.push(`   _Obs: ${it.notes}_`);
  }
  for (const p of premios) {
    if (p.type === "gift") itemLines.push(`• 1x Brinde: ${p.name} - ${p.points} pontos`);
  }

  let pagamentoStr: string;
  if (payload.payLater) {
    pagamentoStr = `Pendente (na ${nomeDaMesa(payload.store?.tableQrPerClient)})`;
  } else if (payload.paymentMixed) {
    const parts = (payload.paymentParts as PaymentPart[]).map((p) => `${p.method}: ${formatCurrency(p.amount)}`);
    pagamentoStr = `Misto (${parts.join(", ")})`;
  } else {
    pagamentoStr = getPaymentMethodLabel(payload.paymentMethod || "");
  }
  if (payload.cashChange > 0) pagamentoStr += ` (troco para ${formatCurrency(payload.cashReceived || 0)})`;

  const modalidadeStr = payload.deliveryMode === "entrega" ? "Entrega" : payload.deliveryMode === "combinar" ? "A combinar" : "Retirada no balcão";

  const addr = payload.address || {};
  const enderecoStr = payload.deliveryMode === "entrega" && (addr.street || addr.neighborhood)
    ? `Endereço: ${addr.street || ""}${addr.number ? ", " + addr.number : ""}${addr.complement ? " - " + addr.complement : ""}${addr.neighborhood ? " - " + addr.neighborhood : ""}${addr.city ? " - " + addr.city : ""}${addr.reference ? " (Ref: " + addr.reference + ")" : ""}`
    : "";

  const observacoesStr = payload.orderNotes || payload.notes || "";

  const customer = payload.customer || {};
  let clienteStr = "";
  if (customer.kind === "existing") clienteStr = customer.customer?.firstName || "";
  else if (customer.kind === "new") clienteStr = customer.name || "";

  const template = store.orderMessageTemplate as string | null | undefined;

  if (template && template.trim()) {
    const totalStr = formatCurrency(payload.total);
    const subtotalStr = formatCurrency(payload.subtotal);
    const descontoStr = payload.discountAmount > 0 ? formatCurrency(payload.discountAmount) : "";
    const acrescimoStr = payload.surcharge > 0 ? formatCurrency(payload.surcharge) : "";
    // Frete pendente: {taxa}/{frete} dizem «a calcular» (vazio parecia entrega grátis).
    const taxaStr = payload.deliveryFeePending === true ? "a calcular" : payload.deliveryFee > 0 ? formatCurrency(payload.deliveryFee) : "";

    const replaced = template
      .replace(/\{itens\}/gi, itemLines.join("\n"))
      .replace(/\{items\}/gi, itemLines.join("\n"))
      .replace(/\{total\}/gi, totalStr)
      .replace(/\{subtotal\}/gi, subtotalStr)
      .replace(/\{desconto\}/gi, descontoStr)
      .replace(/\{acrescimo\}/gi, acrescimoStr)
      .replace(/\{acréscimo\}/gi, acrescimoStr)
      .replace(/\{taxa\}/gi, taxaStr)
      .replace(/\{frete\}/gi, taxaStr)
      .replace(/\{pagamento\}/gi, pagamentoStr)
      .replace(/\{modalidade\}/gi, modalidadeStr)
      .replace(/\{endereco\}/gi, enderecoStr)
      .replace(/\{endereço\}/gi, enderecoStr)
      .replace(/\{observacoes\}/gi, observacoesStr)
      .replace(/\{observações\}/gi, observacoesStr)
      .replace(/\{cliente\}/gi, clienteStr)
      .replace(/\{loja\}/gi, store.name || "")
      .replace(/\{mesa\}/gi, payload.tableNumber || "")
      .replace(/\{numero_pedido\}/gi, payload.orderNumber || "")
      .replace(/\{pedido\}/gi, payload.orderNumber || "");

    return replaced
      .split("\n")
      .map((l) => l.trimEnd())
      .filter((l, i, arr) => !(l === "" && arr[i - 1] === ""))
      .join("\n");
  }

  const lines: string[] = [];
  lines.push(`*${store.name || "Loja"} - Comprovante de venda*`);
  lines.push("");
  lines.push(...itemLines);
  lines.push("");
  lines.push(`*Subtotal:* ${formatCurrency(payload.subtotal)}`);
  if (payload.deliveryFeePending === true) lines.push("*Taxa de entrega:* a calcular");
  else if (payload.deliveryFee > 0) lines.push(`*Taxa de entrega:* ${formatCurrency(payload.deliveryFee)}`);
  if (payload.discountAmount > 0) lines.push(`*Desconto:* -${formatCurrency(payload.discountAmount)}`);
  for (const p of premios) {
    if (p.discount > 0) lines.push(`*Pontos (${p.name}):* -${formatCurrency(p.discount)}`);
  }
  if (payload.surcharge > 0) lines.push(`*Acréscimo (${pagamentoStr}):* +${formatCurrency(payload.surcharge)}`);
  lines.push(`*Total:* ${formatCurrency(payload.total)}`);
  lines.push(`*Pagamento:* ${pagamentoStr}`);
  if (payload.tableNumber) lines.push(`*${NomeDaMesa(payload.store?.tableQrPerClient)}:* ${payload.tableNumber}`);
  lines.push(`*Modalidade:* ${modalidadeStr}`);
  if (enderecoStr) lines.push(enderecoStr);
  if (observacoesStr) lines.push(`*Observações:* ${observacoesStr}`);
  return lines.join("\n");
}

export type CategoryItem = Category;
